import { useMemo } from "react";
import QRCode from "qrcode.react";
import {
  APP_STORE_BADGE_SRC,
  APP_STORE_URL,
  GOOGLE_PLAY_BADGE_SRC,
  GOOGLE_PLAY_URL,
} from "@/constants/appStore";
import type { HelpHubDTO } from "@/types/help-hub/help-hub";
import { buildGenieKeepUrl } from "@/utils/agent/genieShareLinks";
import { getMobilePlatform } from "@/utils/device";
import { HubSection } from "./HubSection";

interface HubAppSectionProps {
  hub: HelpHubDTO;
  displayName: string;
}

/**
 * "Take us with you" — the app door on a Genie Page.
 *
 * The keep link is the same one the share bars already hand out: on a phone
 * with the app installed it drops the Genie straight into My Genies; without
 * the app it lands on `/keep`, which offers the store and a browser
 * conversation instead of dead-ending. On desktop the link becomes a QR so
 * the visitor's phone does the travelling, plus both store badges for anyone
 * who wants the app first.
 */
export const HubAppSection = ({ hub, displayName }: HubAppSectionProps) => {
  const platform = useMemo(() => getMobilePlatform(), []);
  const keepUrl = buildGenieKeepUrl({
    urlName: hub.urlName,
    agentId: hub.genieId,
    name: hub.name,
  });

  if (!keepUrl) return null;

  const { branding } = hub;
  const onPhone = platform !== "other";
  const storeBadges = (
    <div className="flex flex-wrap items-center gap-3">
      {(platform === "ios" || !onPhone) && (
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noreferrer noopener"
          aria-label="Download on the App Store"
          className="inline-flex transition-transform hover:scale-105"
        >
          <img src={APP_STORE_BADGE_SRC} alt="Download on the App Store" className="h-11" />
        </a>
      )}
      {(platform === "android" || !onPhone) && (
        <a
          href={GOOGLE_PLAY_URL}
          target="_blank"
          rel="noreferrer noopener"
          aria-label="Get it on Google Play"
          className="inline-flex transition-transform hover:scale-105"
        >
          <img src={GOOGLE_PLAY_BADGE_SRC} alt="Get it on Google Play" className="h-11" />
        </a>
      )}
    </div>
  );

  return (
    <HubSection
      id="app"
      title="Take us with you"
      subtitle={`Keep ${displayName} in your pocket — answers wherever you are.`}
      genieIcon="genie-lamp"
      branding={branding}
    >
      <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr]">
        <div className="flex flex-col justify-center gap-4 rounded-2xl border border-genie-border bg-white p-6">
          <p className="text-[14.5px] leading-relaxed text-genie-text-sub">
            Get the Help Genie app and {displayName} lands in My Genies — ready to
            answer by voice or text whenever you need it, no browser required.
          </p>
          {onPhone && (
            <a
              href={keepUrl}
              className="inline-flex min-h-[44px] w-fit items-center rounded-xl px-6 py-3 text-[14px] font-semibold text-white"
              style={{ backgroundColor: branding.primaryColor }}
            >
              Keep {displayName} in the app
            </a>
          )}
          {storeBadges}
        </div>

        {/* Desktop: the phone does the travelling. Hidden on phones, where the
            keep link above is one tap instead of a scan. */}
        <div className="hidden flex-col items-center justify-center gap-3 rounded-2xl border border-genie-border bg-white p-6 sm:flex">
          <div
            className="rounded-2xl border p-3"
            style={{ borderColor: `${branding.primaryColor}40` }}
          >
            <QRCode value={keepUrl} size={132} level="M" fgColor="#111827" bgColor="#FFFFFF" />
          </div>
          <p className="text-center text-[12.5px] text-genie-text-muted">
            Scan with your phone to keep {displayName} with you
          </p>
        </div>
      </div>
    </HubSection>
  );
};

export default HubAppSection;
